// Petty cash API (backend routes/pettyCash.routes.ts, mounted at /api/petty-cash). Every call answers
// { success, data }; these helpers return `data`. SAP reads can take a while, hence the longer timeouts.
import apiService from './api.service';
import type {
  AccountingPreview,
  AuxiliaryList,
  CashAccountOption,
  ClaimAttachment,
  ClaimDetail,
  ClaimRow,
  ClaimSaveInput,
  CodeName,
  ConfirmationInput,
  ConfirmationList,
  ConfirmationView,
  FinanceLineChange,
  GlPreview,
  GlPreviewParams,
  HandoverConfirmInput,
  HandoverInput,
  HandoverList,
  HandoverView,
  MyConfirmation,
  PettyCashCategory,
  PettyCashConfig,
  PettyCashHolder,
  PettyCashSettings,
  PettyCashSetup,
  ProjectDefaults,
  ReimburseMethod,
  ReceiptAccount,
  RoutePreview,
  VendorLookup,
  VendorMatch,
} from '../types/pettyCash';
import { fileNameFromDisposition, todayIso } from '../utils/pettyCash';

type Envelope<T> = { success: boolean; data: T; message?: string };
const data = <T>(res: Envelope<T>): T => res.data;
const BASE = '/petty-cash';
const SAP_TIMEOUT = { timeout: 120000 };

/** mine, waiting for me, all (view all), to reimburse (finance posting), originals pending (finance). */
export type ClaimScope = 'mine' | 'waiting' | 'all' | 'reimburse' | 'originals';
export type FinanceAction = 'approve' | 'return' | 'reject' | 'revert';

export interface ReimburseInput {
  amount: number;
  /** YYYY-MM-DD */
  date: string;
  method: ReimburseMethod;
  reference: string | null;
  note: string | null;
}

/**
 * A file the server streams (the Excel export, the voucher PDF): its bytes and its name. A refusal's JSON body
 * arrives as a Blob too: it is read back so the caller shows the server's message, as for any other call.
 */
async function download(url: string, params: Record<string, unknown> | undefined, fallbackName: string): Promise<{ blob: Blob; fileName: string }> {
  try {
    const res = await (apiService as any).axiosInstance.get(url, { params, responseType: 'blob', timeout: 180000 });
    return { blob: res.data as Blob, fileName: fileNameFromDisposition(res.headers?.['content-disposition'], fallbackName) };
  } catch (e: any) {
    const body = e?.response?.data;
    if (body instanceof Blob) {
      let parsed: unknown = {};
      try {
        parsed = JSON.parse(await body.text());
      } catch {
        parsed = {};
      }
      e.response.data = parsed;
    }
    throw e;
  }
}

const pettyCashApi = {
  // ---- configuration and lookups
  async config(companyId: number): Promise<PettyCashConfig> {
    return data(await apiService.get<Envelope<PettyCashConfig>>(`${BASE}/config`, { companyId }, SAP_TIMEOUT));
  },
  /** The projects or departments granted to the holder (the signed-in user when holderUserId is empty). */
  async allocations(companyId: number, type: 'Project' | 'CostCenter', holderUserId?: number | null): Promise<CodeName[]> {
    return data(await apiService.get<Envelope<CodeName[]>>(`${BASE}/allocations`, { companyId, type, holderUserId: holderUserId || undefined }, SAP_TIMEOUT));
  },
  async projectDefaults(companyId: number, code: string): Promise<ProjectDefaults> {
    return data(await apiService.get<Envelope<ProjectDefaults>>(`${BASE}/project-defaults`, { companyId, code }, SAP_TIMEOUT));
  },
  /** A receipt's account for its category on this claim's side and project, and the auxiliaries that account takes. */
  async receiptAccount(params: { companyId: number; allocationType: string; allocationCode: string; projectCode?: string | null; categoryId: number }): Promise<ReceiptAccount> {
    return data(await apiService.get<Envelope<ReceiptAccount>>(`${BASE}/receipt-account`, { ...params, projectCode: params.projectCode || undefined }, SAP_TIMEOUT));
  },
  async routePreview(params: { companyId: number; allocationType: string; allocationCode: string; facilityType: string; cycle: string; holderUserId?: number | null }): Promise<RoutePreview> {
    return data(await apiService.get<Envelope<RoutePreview>>(`${BASE}/route-preview`, params, SAP_TIMEOUT));
  },
  async lookupVendorByVat(companyId: number, vat: string): Promise<VendorLookup> {
    return data(await apiService.get<Envelope<VendorLookup>>(`${BASE}/vendors/lookup`, { companyId, vat }, SAP_TIMEOUT));
  },
  async searchVendors(companyId: number, q: string): Promise<VendorMatch[]> {
    return data(await apiService.get<Envelope<VendorMatch[]>>(`${BASE}/vendors/search`, { companyId, q }, SAP_TIMEOUT));
  },
  async searchAccounts(companyId: number, q: string): Promise<CodeName[]> {
    return data(await apiService.get<Envelope<CodeName[]>>(`${BASE}/accounts`, { companyId, q }, SAP_TIMEOUT));
  },
  async cashAccounts(companyId: number): Promise<CashAccountOption[]> {
    return data(await apiService.get<Envelope<CashAccountOption[]>>(`${BASE}/cash-accounts`, { companyId }, SAP_TIMEOUT));
  },
  /** The auxiliaries (Dimension 5 codes) of an account's type, or of a type, or all of them. */
  async auxiliaries(companyId: number, opts: { account?: string; type?: string } = {}): Promise<AuxiliaryList> {
    return data(await apiService.get<Envelope<AuxiliaryList>>(`${BASE}/auxiliaries`, { companyId, ...opts }, SAP_TIMEOUT));
  },
  async dimensionCodes(companyId: number, dimension: number): Promise<CodeName[]> {
    return data(await apiService.get<Envelope<CodeName[]>>(`${BASE}/dimension-codes`, { companyId, dimension }, SAP_TIMEOUT));
  },

  // ---- claims
  async listClaims(companyId: number, scope: ClaimScope): Promise<ClaimRow[]> {
    return data(await apiService.get<Envelope<ClaimRow[]>>(`${BASE}/claims`, { companyId, scope }, { timeout: 60000 }));
  },
  async getClaim(claimId: number): Promise<ClaimDetail> {
    return data(await apiService.get<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}`, undefined, SAP_TIMEOUT));
  },
  async createClaim(input: ClaimSaveInput): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims`, input, SAP_TIMEOUT));
  },
  async saveClaim(claimId: number, input: ClaimSaveInput): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/save`, input, SAP_TIMEOUT));
  },
  async submitClaim(claimId: number): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/submit`, {}, SAP_TIMEOUT));
  },
  async cancelClaim(claimId: number, comment?: string): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/cancel`, { comment }, SAP_TIMEOUT));
  },
  /** PR approval stage: approve, reject (comment required) or revert to the previous stage / the holder. */
  async decide(claimId: number, action: 'approve' | 'reject' | 'revert', comment?: string): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/decision`, { action, comment }, SAP_TIMEOUT));
  },
  async accounting(claimId: number, postingDate?: string): Promise<AccountingPreview> {
    return data(await apiService.get<Envelope<AccountingPreview>>(`${BASE}/claims/${claimId}/accounting`, { postingDate }, SAP_TIMEOUT));
  },
  async financeLine(claimId: number, lineId: number, change: FinanceLineChange): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/lines/${lineId}/finance`, change, SAP_TIMEOUT));
  },
  /**
   * Finance stages: approve (the last stage starts the SAP posting with the posting date; an earlier one passes the
   * claim to the next stage), return to the holder, reject, or revert to the previous finance stage (comment required).
   */
  async finance(claimId: number, action: FinanceAction, comment?: string, postingDate?: string): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/finance`, { action, comment, postingDate }, SAP_TIMEOUT));
  },
  /** Records how the holder was paid back for a posted claim (and posts the top-up journal when the setup says so). */
  async reimburse(claimId: number, input: ReimburseInput): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/reimburse`, input, SAP_TIMEOUT));
  },
  /** Posts again the top-up journal SAP refused (or that hangs). */
  async retryReimbursement(claimId: number): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/reimburse/retry`, {}, SAP_TIMEOUT));
  },
  /** Marks the original receipts as received (with the transmittal number), or removes the mark (received false). */
  async markOriginals(claimId: number, input: { received: boolean; reference?: string | null; note?: string | null }): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/originals`, input, SAP_TIMEOUT));
  },
  /** The petty cash voucher (PDF) of a submitted claim. */
  async downloadVoucher(claimId: number, claimNo?: string | null): Promise<{ blob: Blob; fileName: string }> {
    return download(`${BASE}/claims/${claimId}/voucher`, undefined, `petty-cash-voucher-${claimNo || claimId}.pdf`);
  },
  /** The list of a scope as an Excel workbook. */
  async exportClaims(companyId: number, scope: ClaimScope): Promise<{ blob: Blob; fileName: string }> {
    return download(`${BASE}/claims/export`, { companyId, scope }, `petty-cash-claims-${scope}-${todayIso()}.xlsx`);
  },
  /**
   * Posts again what SAP lacks (after finance corrected the refused receipts). A new posting date applies to what is
   * left; it may not be earlier than the claim's. 422 lists what must be fixed first.
   */
  async retry(claimId: number, postingDate?: string): Promise<ClaimDetail> {
    return data(await apiService.post<Envelope<ClaimDetail>>(`${BASE}/claims/${claimId}/retry`, postingDate ? { postingDate } : {}, SAP_TIMEOUT));
  },

  // ---- receipts and files
  async uploadAttachment(claimId: number, file: File, lineId?: number | null): Promise<ClaimAttachment> {
    const form = new FormData();
    form.append('file', file);
    if (lineId) form.append('lineId', String(lineId));
    const res = await (apiService as any).axiosInstance.post(`${BASE}/claims/${claimId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return data(res.data as Envelope<ClaimAttachment>);
  },
  async downloadAttachment(attachmentId: number): Promise<Blob> {
    const res = await (apiService as any).axiosInstance.get(`${BASE}/attachments/${attachmentId}`, { responseType: 'blob', timeout: 120000 });
    return res.data as Blob;
  },
  async deleteAttachment(attachmentId: number): Promise<void> {
    await apiService.delete<Envelope<unknown>>(`${BASE}/attachments/${attachmentId}`);
  },

  // ---- setup (Petty Cash Setup page)
  async setup(companyId: number): Promise<PettyCashSetup> {
    return data(await apiService.get<Envelope<PettyCashSetup>>(`${BASE}/setup`, { companyId }, SAP_TIMEOUT));
  },
  async saveSettings(companyId: number, settings: PettyCashSettings): Promise<PettyCashSettings> {
    return data(await apiService.put<Envelope<PettyCashSettings>>(`${BASE}/setup/settings`, { companyId, ...settings }));
  },
  async saveHolder(companyId: number, holder: { holderId?: number; userId: number; sapAccount: string; auxiliaryCode: string | null; floatAmount: number | null; isActive: boolean }): Promise<PettyCashHolder> {
    return data(await apiService.post<Envelope<PettyCashHolder>>(`${BASE}/setup/holders`, { companyId, ...holder }, SAP_TIMEOUT));
  },
  async saveCategory(
    companyId: number,
    category: Omit<PettyCashCategory, 'categoryId' | 'projectAccountName' | 'costCenterAccountName' | 'projectItemName' | 'deptItemName'> & { categoryId?: number },
  ): Promise<PettyCashCategory> {
    return data(await apiService.post<Envelope<PettyCashCategory>>(`${BASE}/setup/categories`, { companyId, ...category }, SAP_TIMEOUT));
  },
  /** What SAP's Advanced G/L rules give a category's item or class: per warehouse type for projects, and for the department warehouse. */
  async glPreview(companyId: number, params: GlPreviewParams): Promise<GlPreview> {
    const query: Record<string, string | number> = { companyId };
    for (const [k, v] of Object.entries(params)) if (v) query[k] = v;
    return data(await apiService.get<Envelope<GlPreview>>(`${BASE}/setup/gl-preview`, query, SAP_TIMEOUT));
  },
  /** Adds the study's starter categories (KSA chart of accounts) that the company lacks; accounts SAP doesn't hold are left empty. */
  async loadStarterCategories(companyId: number): Promise<{ added: number; skipped: number; categories: PettyCashCategory[] }> {
    return data(await apiService.post<Envelope<{ added: number; skipped: number; categories: PettyCashCategory[] }>>(`${BASE}/setup/categories/starter`, { companyId }, SAP_TIMEOUT));
  },

  // ---- handovers (a holder hands his petty cash to a successor)
  /** Setup and finance see every handover of the company; others only theirs. */
  async handovers(companyId: number): Promise<HandoverList> {
    return data(await apiService.get<Envelope<HandoverList>>(`${BASE}/handovers`, { companyId }, { timeout: 60000 }));
  },
  async startHandover(input: HandoverInput): Promise<HandoverView> {
    return data(await apiService.post<Envelope<HandoverView>>(`${BASE}/handovers`, input, SAP_TIMEOUT));
  },
  /** The outgoing holder confirms the cash counted, then the successor takes it over (which completes the handover). */
  async confirmHandover(handoverId: number, input: HandoverConfirmInput): Promise<HandoverView> {
    return data(await apiService.post<Envelope<HandoverView>>(`${BASE}/handovers/${handoverId}/confirm`, input, SAP_TIMEOUT));
  },
  async cancelHandover(handoverId: number, reason: string): Promise<HandoverView> {
    return data(await apiService.post<Envelope<HandoverView>>(`${BASE}/handovers/${handoverId}/cancel`, { reason }, { timeout: 60000 }));
  },

  // ---- quarterly confirmations (each holder confirms the cash in his custody)
  /** Every holder's confirmation of a quarter ('2026-Q4'; empty = the current one). Finance or setup. */
  async confirmations(companyId: number, quarter?: string): Promise<ConfirmationList> {
    return data(await apiService.get<Envelope<ConfirmationList>>(`${BASE}/confirmations`, { companyId, quarter: quarter || undefined }, { timeout: 60000 }));
  },
  /** The signed-in holder's: the quarter due, his confirmation of it, and his figures. */
  async myConfirmation(companyId: number): Promise<MyConfirmation> {
    return data(await apiService.get<Envelope<MyConfirmation>>(`${BASE}/confirmations/mine`, { companyId }, { timeout: 60000 }));
  },
  /** One per holder and quarter: a second one for the same quarter answers 409. */
  async confirm(input: ConfirmationInput): Promise<ConfirmationView> {
    return data(await apiService.post<Envelope<ConfirmationView>>(`${BASE}/confirmations`, input, { timeout: 60000 }));
  },
  async reviewConfirmation(confirmationId: number, note: string | null): Promise<ConfirmationView> {
    return data(await apiService.post<Envelope<ConfirmationView>>(`${BASE}/confirmations/${confirmationId}/review`, { note }, { timeout: 60000 }));
  },
};

export default pettyCashApi;
